import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback(({
        type = 'info',
        title,
        message,
        duration = 5000
    }) => {
        const id = Date.now() + Math.random();
        const newToast = {
            id,
            type,
            title,
            message,
            duration
        };

        setToasts(prev => [...prev, newToast]);
        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    // Convenience methods for different toast types
    const showSuccess = useCallback((message, title = 'Success', duration = 5000) => {
        return addToast({ type: 'success', title, message, duration });
    }, [addToast]);

    const showError = useCallback((message, title = 'Error', duration = 7000) => {
        return addToast({ type: 'error', title, message, duration });
    }, [addToast]);

    const showWarning = useCallback((message, title = 'Warning', duration = 6000) => {
        return addToast({ type: 'warning', title, message, duration });
    }, [addToast]);

    const showInfo = useCallback((message, title = '', duration = 5000) => {
        return addToast({ type: 'info', title, message, duration });
    }, [addToast]);

    const value = {
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
        showSuccess,
        showError,
        showWarning,
        showInfo
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
